import type {
  Beat,
  CameraWaypoint,
  DeskLoginConfig,
  DesktopConfig,
  HolidayTabletConfig,
  InboxTriageConfig,
  PersonalLaptopConfig,
  ScenarioSpec,
  ScoringEvent,
  UsbDeviceConfig,
} from "./types";
import { officeUnderAttackScenario } from "./office-under-attack.scenario";
import { holidayCallScenario } from "./holiday-call.scenario";

/**
 * Breach follow-up course. The reception terminal is a hub: two required
 * conference-TV teaching decks plus four optional, individually-selectable
 * live exercises. Every spoke returns to the hub; the final quiz only covers
 * the required teaching.
 */

const OUA_ENV = officeUnderAttackScenario.scene.environment;
const HOLIDAY_ENV = holidayCallScenario.scene.environment;
const OUA_EVENTS = officeUnderAttackScenario.voiceAttackers.office_under_attack.scoringEvents;

function deviceConfig<T>(scenario: ScenarioSpec, id: string): T {
  const device = scenario.devices.find((item) => item.id === id);
  if (!device) throw new Error(`device "${id}" not found in ${scenario.id}`);
  return device.config as T;
}

function beat(id: string): Beat {
  const value = officeUnderAttackScenario.beats.find((item) => item.id === id);
  if (!value) throw new Error(`office-under-attack beat "${id}" not found`);
  return value;
}

function waypoint(scenario: ScenarioSpec, id: string, newId = id, durationS?: number): CameraWaypoint {
  const value = scenario.scene.waypoints.find((item) => item.id === id);
  if (!value) throw new Error(`${scenario.id} waypoint "${id}" not found`);
  return { ...value, id: newId, ...(durationS == null ? {} : { durationS }) };
}

function scoring(...ids: string[]): ScoringEvent[] {
  return ids.map((id) => {
    const value = OUA_EVENTS.find((item) => item.id === id);
    if (!value) throw new Error(`office-under-attack scoring event "${id}" not found`);
    return value;
  });
}

const OFFICE_ENV = {
  gltfUrl: OUA_ENV.gltfUrl,
  gltfScale: OUA_ENV.gltfScale,
  gltfPosition: OUA_ENV.gltfPosition,
  gltfRotationDeg: OUA_ENV.gltfRotationDeg,
  gltfColliders: OUA_ENV.gltfColliders,
  procedural: OUA_ENV.procedural,
  enableSplat: false,
  enablePano: false,
  backdropColor: OUA_ENV.backdropColor,
  exposure: OUA_ENV.exposure,
  splat: {},
  deskPc: OUA_ENV.deskPc,
  receptionTerminal: OUA_ENV.receptionTerminal,
  conferenceTv: OUA_ENV.conferenceTv,
  deskPackage: OUA_ENV.deskPackage,
  privacyRoom: {
    items: [],
    furniture: OUA_ENV.privacyRoom?.furniture?.filter((item) => item.kind === "desk" || item.kind === "laptop"),
  },
};

const deskLogin = deviceConfig<DeskLoginConfig>(officeUnderAttackScenario, "desklogin_decision");
const baseDesktop = deviceConfig<DesktopConfig>(officeUnderAttackScenario, "desktop_home");
const baseInbox = deviceConfig<InboxTriageConfig>(officeUnderAttackScenario, "inbox_triage");
const personalLaptopBase = deviceConfig<PersonalLaptopConfig>(officeUnderAttackScenario, "personal_laptop");

const phishingDesktop: DesktopConfig = {
  ...baseDesktop,
  pdfBriefing: {
    fileName: "Why_Phishing_Matters_After_A_Breach.pdf",
    title: "Phishing: one route to an exposed password",
    subtitle: "Your breach notice cannot tell us which route was used. This exercise shows one of the most common.",
    legitDomain: "northbayfinancial.com",
    legitLoginUrl: "https://portal.northbayfinancial.com/login",
    sections: [
      {
        heading: "How this connects to the notice at reception",
        body:
          "A phishing message sends you to a convincing copy of a login page. If a password is entered there, the attacker receives it immediately. That is one possible explanation for a credential in a breach dump — but malware or a breached third party could produce the same result.",
        bullets: [
          "A familiar logo and a padlock do not prove who owns a page.",
          "The exact registered domain is the identity check that matters.",
          "A password manager refusing to fill is a warning: the domain changed.",
        ],
      },
    ],
    redFlags: [
      "A sender domain or Reply-To that is almost, but not exactly, right.",
      "Urgency or authority used to make verification feel inconvenient.",
      "A link, QR code or attachment that unexpectedly leads to a login.",
      "A request to enable macros or run a downloaded file.",
    ],
    continueLabel: "Understood — open the inbox",
  },
};

const downloadLaptop: PersonalLaptopConfig = {
  ...personalLaptopBase,
  ownerNote: "Your own laptop — not a company device. Read the safety briefing before you search.",
  entryMode: "briefing",
  outcomeMode: "infostealer",
  briefing: {
    fileName: "Safe_Downloads_and_Search_Results.pdf",
    title: "When a search result delivers malware",
    subtitle: "Attackers buy ads and copy trusted download pages so the dangerous result can look like the normal one.",
    legitDomain: "vendor.example",
    legitLoginUrl: "The software vendor or your company software portal",
    sections: [
      {
        heading: "How the fake result gets in front of you",
        body:
          "Attackers use search advertising and search-engine poisoning to place a malicious result above, or beside, the genuine vendor. The page copies the product name, screenshots and download button. The file works just well enough to avoid suspicion while an infostealer runs in the background.",
        bullets: [
          "Sponsored does not mean security-checked.",
          "Read the domain before downloading, not only the result title.",
          "A browser game, document or video should not need an executable launcher.",
        ],
      },
    ],
    redFlags: [
      "A result that promises a paid product, game or media for free.",
      "A download from a lookalike domain rather than the publisher.",
      "A web page demanding an update or launcher before content opens.",
      "An executable file when you expected a document, image or video.",
    ],
    continueLabel: "Got it — open the browser",
  },
};

const holidayWifi: HolidayTabletConfig = {
  ...deviceConfig<HolidayTabletConfig>(holidayCallScenario, "holiday_wifi"),
  continueLabel: "Return to the breach impact hub",
};

export const pcToDarkWebScenario: ScenarioSpec = {
  version: 1,
  id: "pc-to-darkweb",
  title: "From Your PC to the Dark Web",
  summary: {
    goal: "Understand how your work password can end up in criminal hands, what a criminal does with it, and the few simple habits that protect you.",
    youDo: "Watch a short film, then two short lessons on the conference-room TV. If you want, practise with four hands-on games: a work inbox, a game download, a hotel Wi-Fi choice and a USB stick on your desk.",
    duration: "Core 8-10 min, labs add 15-20 min",
    tags: ["leaked_credentials", "passwords", "email_phishing", "malware", "public_wifi", "usb_devices"],
    audience: ["all_staff"],
    needs: ["sound"],
  },
  topic: "How a work password gets stolen, what criminals do with it, and how to protect yourself",
  language: "en",
  ambientUrl: "/audio/gymnopedie-kevin-macleod.mp3",
  ambientVolume: 0.14,
  music: "none",
  riskMeter: false,
  exposureMap: true,
  passThreshold: 60,
  allowFreeExplore: false,

  scene: {
    environment: OFFICE_ENV,
    environments: [
      { id: "office", environment: OFFICE_ENV, ambientUrl: "/audio/gymnopedie-kevin-macleod.mp3", ambientVolume: 0.14, music: "none" },
      {
        id: "pool",
        environment: HOLIDAY_ENV,
        ambientUrl: holidayCallScenario.ambientUrl,
        ambientVolume: holidayCallScenario.ambientVolume,
        music: holidayCallScenario.music,
      },
    ],
    props: [],
    waypoints: [
      waypoint(officeUnderAttackScenario, "wp_reception_wide"),
      waypoint(officeUnderAttackScenario, "wp_terminal_close"),
      waypoint(officeUnderAttackScenario, "wp_desk_signin"),
      waypoint(officeUnderAttackScenario, "wp_desk_glide"),
      waypoint(officeUnderAttackScenario, "wp_privacy_entrance"),
      waypoint(officeUnderAttackScenario, "wp_lounge_laptop"),
      waypoint(officeUnderAttackScenario, "wp_meeting_establish"),
      {
        ...waypoint(officeUnderAttackScenario, "wp_meeting_tv_close"),
        // Closer, dead-on conference-TV framing for the teaching decks. At
        // fov 44 and ~3.3m distance the 1.45m panel fills ~55% viewport height.
        position: [-9.0, 1.8, 3.86],
        target: [-12.3, 1.8, 3.86],
      },
      waypoint(holidayCallScenario, "wp_pool_wide", "wp_breach_pool_wide", 0),
      waypoint(holidayCallScenario, "wp_pool_ipad", "wp_breach_pool_ipad"),
    ],
    initialWaypointId: "wp_reception_wide",
  },

  devices: [
    {
      id: "breach_hub",
      kind: "breachbrief",
      config: {
        title: "From your PC to the dark web",
        offLabel: "Switch on the welcome terminal",
        progressKey: "pc-to-darkweb",
        videoFirst: true,
        lines: [
          "Security notice: your work email and password were found on a list of stolen logins that criminals share and sell online.",
          "This is not automatically your fault. A password can get out in several ways, and often the leak happens at another company, not on your computer.",
          "Start with the film at the top. It explains the dark web, how a login gets out, and what you should do. The two lessons under it go through the same ideas, one step at a time.",
        ],
        hubIntro: "Your work login was found among stolen logins. Watch the film, then the two short lessons.",
        hubVideo: {
          title: "Stolen logins",
          blurb: "About six minutes. What the dark web is, how a login gets out, what criminals do with it, and what you should do.",
          src: "/cdn/sim/awareness-videos/stolen-logins/stolen-logins.mp4",
          poster: "/cdn/sim/awareness-videos/stolen-logins/wide/plate.jpg",
        },
        cardsLabel: "Please watch both — shown on the conference-room TV",
        cardsTitle: "What you need to know",
        cardsIntro: "Two short lessons in plain language.",
        cards: [
          {
            id: "deck_how",
            label: "How does a password get out?",
            blurb: "The common ways a password ends up in criminal hands.",
            key: "deck_how",
            completionBeatId: "b_done_deck_how",
            icon: "slides",
            hint: "6 slides",
            imageUrl: "/cdn/sim/pc-to-darkweb/cards/lesson-stolen.jpg",
          },
          {
            id: "deck_value",
            label: "What do criminals do with it?",
            blurb: "What one stolen login lets a criminal do to you and the company.",
            key: "deck_value",
            completionBeatId: "b_done_deck_value",
            icon: "slides",
            hint: "5 slides",
            imageUrl: "/cdn/sim/pc-to-darkweb/cards/lesson-darkweb.jpg",
          },
        ],
        exercisesKicker: "4 interactive games",
        exercisesLabel: "Experience real attack paths",
        exercisesIntro:
          "Each route is a self-contained, gamified simulation. Choose one, make the decisions yourself, and return here for another.",
        exercises: [
          {
            id: "lab_phishing",
            label: "Inbox investigation",
            blurb: "Spot the phishing email.",
            key: "lab_phishing",
            completionBeatId: "b_done_phishing",
            icon: "phishing",
            hint: "Play game",
            imageUrl: "/cdn/sim/pc-to-darkweb/cards/inbox.jpg",
          },
          {
            id: "lab_download",
            label: "Download trap",
            blurb: "Check before you install.",
            key: "lab_download",
            completionBeatId: "b_done_download",
            icon: "download",
            hint: "Play game",
            imageUrl: "/cdn/sim/pc-to-darkweb/cards/download.jpg",
          },
          {
            id: "lab_wifi",
            label: "Rogue Wi-Fi",
            blurb: "Choose a network you can trust.",
            key: "lab_wifi",
            completionBeatId: "b_done_wifi",
            icon: "wifi",
            hint: "Play game",
            imageUrl: "/cdn/sim/pc-to-darkweb/cards/wifi.jpg",
          },
          {
            id: "lab_usb",
            label: "BadUSB",
            blurb: "Handle an unexpected device.",
            key: "lab_usb",
            completionBeatId: "b_done_usb",
            icon: "usb",
            hint: "Play game",
            imageUrl: "/cdn/sim/pc-to-darkweb/cards/usb.jpg",
          },
        ],
        finishLabel: "Finish the briefing",
        finishHint: "Continue to the short knowledge check",
      },
    },
    {
      id: "deck_how",
      kind: "slidedeck",
      config: {
        deckLabel: "LESSON 1 OF 2",
        title: "How a password gets out",
        continueLabel: "Lesson complete",
        slides: [
          {
            kicker: "WHY YOU ARE HERE",
            title: "Your work login is in criminal hands",
            body:
              "Your work email and password turned up on a list of stolen logins that criminals buy, sell and trade. We do not know yet how it got there, and it is not automatically something you did wrong.",
            bullets: [
              "Why it matters: with your password, someone can sign in as you and reach your email, files and company systems.",
              "This lesson: the four common ways a password gets out, so you know what to watch for.",
            ],
            narration:
              "Your work email and password were found on a list of stolen logins that criminals share online. That does not mean you did something wrong. It does mean someone could sign in as you, so let us look at how a password gets out.",
            visual: "login_found",
          },
          {
            kicker: "WAY 1 — A FAKE LOGIN PAGE",
            title: "You typed it on a copied page",
            body:
              "An email, text or chat message sends you to a page that looks exactly like a login you trust. You type your password, but it goes straight to the criminal instead of the real company.",
            bullets: [
              "The page can copy the real logo and still be fake. The padlock only means the page is private, not honest.",
              "Safer habit: don’t sign in through a link. Open the app, or type the address you know, or use your own saved bookmark.",
            ],
            narration:
              "The first way is a fake login page. A message sends you to a page that looks real, you type your password, and it goes to the criminal. The safest habit is to never sign in through a link. Open the app or your own saved bookmark instead.",
            visual: "phishing_page",
          },
          {
            kicker: "WAY 2 — A HIDDEN PROGRAM",
            title: "A program on a computer copied it",
            body:
              "A free game, a cracked program, a fake software update or an email attachment can secretly install a hidden program that copies every password saved in the browser. This can happen on a work or a home or a shared computer, even one a family member uses.",
            bullets: [
              "The thing you downloaded may really work, while the hidden part quietly steals in the background.",
              "Safer habit: install software only from the official store, the maker, or your IT team, and keep work accounts off shared or family computers.",
            ],
            narration:
              "The second way is a hidden program. A free game, a cracked tool or a fake update can secretly copy the passwords saved in a browser, on a work, home or shared computer. Only install software from the official store, the maker, or IT.",
            visual: "credential_vault",
          },
          {
            kicker: "WAY 3 — ANOTHER COMPANY WAS HACKED",
            title: "A website you use got hacked",
            body:
              "A shop, app or website where you once made an account gets hacked, and the criminals take its whole list of users. If you used your work password there too, that password is now out — through no fault of your own.",
            bullets: [
              "You could not have stopped their hack.",
              "Safer habit: use a different password for every account, so one leak never opens the others. A password manager remembers them for you.",
            ],
            narration:
              "The third way is a hack at another company. A website where you once made an account gets breached and its user list is stolen. If you reused your work password there, it is now out. Use a different password everywhere so one leak cannot open the rest.",
            visual: "third_party_breach",
          },
          {
            kicker: "WAY 4 — WATCHED OR TRICKED",
            title: "Someone watched or simply asked",
            body:
              "Someone can read your password over your shoulder on a train or in a hotel lobby, find it on a sticky note, use a shared computer you left signed in, or phone you pretending to be IT and ask for it.",
            bullets: [
              "Real IT will never ask you for your password or a sign-in code.",
              "Safer habit: keep your screen and notes private, sign out of shared computers, and verify an unexpected caller through a number you already know.",
            ],
            narration:
              "The fourth way needs no technology at all. Someone reads your password over your shoulder, finds it on a note, or phones pretending to be IT and asks for it. Real IT never asks for your password. Keep it private and verify unexpected callers.",
            visual: "human_social",
          },
          {
            kicker: "WHAT TO DO NOW",
            title: "Three simple steps",
            body:
              "You can close the door quickly. The exact way it leaked matters less than acting fast once you know a password is out.",
            bullets: [
              "Change the password here and anywhere else you used the same one.",
              "Turn on two-step sign-in (a code or an app tap) so a password alone is not enough, and tell IT if a hidden program might be on your computer so they can sign you out everywhere.",
            ],
            narration:
              "So what do you do now? Change the password here and anywhere you reused it. Turn on two-step sign-in so a password alone is not enough. And tell IT if a hidden program might be on your computer. Acting fast matters more than being perfect.",
            visual: "protect_steps",
          },
        ],
      },
    },
    {
      id: "deck_value",
      kind: "slidedeck",
      config: {
        deckLabel: "LESSON 2 OF 2",
        title: "What criminals do with a stolen login",
        continueLabel: "Lesson complete",
        slides: [
          {
            kicker: "THE DARK WEB",
            title: "A hidden shop for stolen logins",
            body:
              "The dark web is a hidden part of the internet you cannot find with Google and that is built to keep people anonymous. Criminals use it like an online shop: stolen logins are listed with prices, ratings and sellers.",
            bullets: [
              "Your login can be sold many times, and later handed out for free.",
              "By the time a warning like this reaches you, the login is often already out there.",
            ],
            narration:
              "The dark web is a hidden part of the internet, built to keep people anonymous. Criminals use it like a shop, with listings, prices and ratings. Stolen logins are the goods, and yours can be sold many times over.",
            visual: "dark_market",
          },
          {
            kicker: "WHAT THEY DO — STEP 1",
            title: "They try your password everywhere",
            body:
              "Most people reuse the same password. Criminals know this, so software automatically tries your email and password on many websites at once: webmail, banking, shopping, remote access and social media.",
            bullets: [
              "No one types it by hand. A program tests hundreds of sites in seconds.",
              "Every site where you reused that password now opens too.",
            ],
            narration:
              "First, they try your password everywhere. Because most people reuse passwords, a program automatically tests your email and password across many sites at once. Every place you reused it suddenly opens.",
            visual: "credential_stuffing",
          },
          {
            kicker: "WHAT THEY DO — STEP 2",
            title: "They skip the extra sign-in check",
            body:
              "When you stay signed in, your device holds a small key that proves you already logged in. If a hidden program steals that key, a criminal can load your account without the password and without the phone code.",
            bullets: [
              "Two-step sign-in is still very important and stops most attacks.",
              "But if your computer is infected, sign out of everything and tell IT so the stolen key stops working.",
            ],
            narration:
              "Second, they try to skip the extra check. When you stay signed in, your device keeps a small key that proves you already logged in. Steal that key and a criminal gets in without the password or the phone code, so sign out everywhere if your computer is infected.",
            visual: "session_cookie",
          },
          {
            kicker: "WHAT THEY DO — STEP 3",
            title: "They become you",
            body:
              "Once inside your real mailbox, a criminal has your real name, your past emails and your contacts. They can send believable messages that look exactly like they came from you.",
            bullets: [
              "Fake invoices and payment changes that look like a normal work email.",
              "Access to contracts and customer data, blackmail, and your colleagues becoming the next targets.",
            ],
            narration:
              "Third, they become you. Inside your real mailbox they have your name, your past emails and your contacts. They can send fake invoices, reach contracts and customer data, and target your colleagues, all from an address people already trust.",
            visual: "mailbox_trust",
          },
          {
            kicker: "WHAT THEY DO — STEP 4",
            title: "They sell the way in",
            body:
              "Often the person who steals a login does not use it. They sell the working access to other criminals: fraud gangs, data thieves, or ransomware groups that can lock an entire company.",
            bullets: [
              "One staff login can become the door a whole attack walks through weeks later.",
              "Reporting fast lets the company shut that door before it is used.",
            ],
            narration:
              "Fourth, they sell the way in. Often the thief does not use your login at all. They sell the working access to fraud gangs, data thieves or ransomware groups. One staff login can be the door a whole attack walks through, which is why reporting fast matters.",
            visual: "access_resale",
          },
        ],
      },
    },
    { id: "desklogin_decision", kind: "desklogin", config: deskLogin },
    { id: "phishing_desktop", kind: "desktop", config: phishingDesktop },
    {
      id: "inbox_triage",
      kind: "inboxtriage",
      config: {
        ...baseInbox,
        minVerdicts: 3,
        incompleteHint: "Rate at least three messages before you submit. More review produces a better result.",
      },
    },
    { id: "download_laptop", kind: "personallaptop", config: downloadLaptop },
    { id: "holiday_wifi", kind: "holidaytablet", config: holidayWifi },
    { id: "usb_decision", kind: "usbdevice", config: deviceConfig<UsbDeviceConfig>(officeUnderAttackScenario, "usb_decision") },
    { id: "usb_recovery", kind: "usbdevice", config: deviceConfig<UsbDeviceConfig>(officeUnderAttackScenario, "usb_recovery") },
  ],

  voiceAttackers: {
    pc_to_darkweb: {
      persona: { name: "Attacker", role: "Credential thief", pretext: "it_support", language: "en", voiceId: "onyx" },
      goal: "Obtain and monetise a working staff login.",
      opening: "",
      allowedTopics: [],
      scoringEvents: [
        ...scoring(
          "triage_thorough", "triage_partial", "triage_poor",
          "inbox_link_opened_risky", "inbox_submitted_credentials_phishing",
          "inbox_downloaded_from_phishing_page", "inbox_used_legitimate_login",
          "inbox_attachment_opened_risky", "inbox_activated_malicious_attachment",
          "qr_entered_credentials", "qr_avoided_phishing_qr",
          "concealed_qr_credential_entry", "delayed_reporting_qr_credentials",
          "reported_qr_credential_entry", "recognized_risky_download",
          "believed_risky_download_safe", "inserted_usb_workstation",
          "inserted_usb_personal", "reported_suspicious_usb", "hid_infected_usb",
          "removed_usb_no_report", "restarted_without_reporting", "reported_after_mistake",
        ),
        {
          id: "joined_open_hotel_wifi",
          label: "Joined an unverified open hotel network",
          kind: "risk",
          weight: 15,
          dimension: "identity",
          detail: "A copied Wi-Fi name can place the attacker between the device and the internet, enabling redirects and certificate traps.",
        },
      ],
    },
  },

  beats: [
    {
      id: "b_intro",
      type: "intro",
      title: "From Your PC\nto the Dark Web",
      kicker: "Breach follow-up",
      icon: "shield",
      ctaLabel: "Open the security notice",
      guide: {
        title: "Your work login was found among stolen logins",
        body: "Your work email and password were found on a list of stolen logins that criminals share online. You start at the reception terminal, which explains in plain language how a password gets out and what a criminal does with it, then lets you practise with four short games.",
      },
    },
    { id: "b_cam_terminal", type: "camera", waypointId: "wp_terminal_close", walkSfx: true },
    {
      id: "b_hub",
      type: "device",
      deviceId: "breach_hub",
      guide: { title: "Security briefing", body: "Start with the film, then the two short lessons." },
    },

    { id: "b_deck_how_title", type: "title", environmentId: "office", title: "How does a password get out?", autoAdvanceMs: 1500, guide: { title: "Lesson 1 of 2", body: "The lesson is on the conference-room TV." } },
    { id: "b_cam_meeting_how", type: "camera", waypointId: "wp_meeting_establish", walkSfx: true },
    { id: "b_cam_tv_how", type: "camera", waypointId: "wp_meeting_tv_close" },
    { id: "b_deck_how", type: "device", deviceId: "deck_how", guide: { title: "How a password gets out", body: "Go through the six slides." } },
    { id: "b_done_deck_how", type: "title", title: "Lesson 1 complete", autoAdvanceMs: 850, guide: { title: "Complete", body: "Returning to the briefing hub." } },

    { id: "b_deck_value_title", type: "title", environmentId: "office", title: "What do criminals do with it?", autoAdvanceMs: 1500, guide: { title: "Lesson 2 of 2", body: "The lesson is on the conference-room TV." } },
    { id: "b_cam_meeting_value", type: "camera", waypointId: "wp_meeting_establish", walkSfx: true },
    { id: "b_cam_tv_value", type: "camera", waypointId: "wp_meeting_tv_close" },
    { id: "b_deck_value", type: "device", deviceId: "deck_value", guide: { title: "What criminals do with it", body: "Go through the five slides." } },
    { id: "b_done_deck_value", type: "title", title: "Lesson 2 complete", autoAdvanceMs: 850, guide: { title: "Complete", body: "Returning to the briefing hub." } },

    { id: "b_phishing_title", type: "title", environmentId: "office", title: "Live lab — Phishing", autoAdvanceMs: 1400, guide: { title: "Phishing lab", body: "Investigate the inbox yourself." } },
    { id: "b_cam_signin", type: "camera", waypointId: "wp_desk_signin", walkSfx: true },
    { id: "b_desklogin", type: "device", deviceId: "desklogin_decision", guide: { title: "Your desk", body: "Click the screen and sign in." }, completeOn: { action: "continue" } },
    { id: "b_desktop_pdf", type: "device", deviceId: "phishing_desktop", guide: { title: "Connect this to your breach notice", body: "Open the pulsing briefing PDF first." }, completeOn: { action: "open_app", arg: "pdf_done" } },
    { id: "b_desktop_mail", type: "device", deviceId: "phishing_desktop", guide: { title: "Open the inbox", body: "Click Outlook and investigate at least three messages." }, completeOn: { action: "open_app", arg: "outlook" } },
    { id: "b_cam_inbox", type: "camera", waypointId: "wp_desk_signin" },
    { id: "b_inbox_triage", type: "device", deviceId: "inbox_triage", guide: { title: "Rate at least three messages", body: "Inspect sender details and destinations before you decide." } },
    {
      id: "b_teach_phishing_risky",
      type: "teach",
      teach: {
        outcome: "risky",
        body:
          "This lab showed how a credential can reach a breach dump through phishing. The message creates urgency, the destination copies a trusted login, and any password entered there is delivered to the attacker in real time.",
        spot: ["The actual sender and Reply-To, not the display name.", "The exact registered domain before any login.", "An unexpected sign-in reached from email, SMS, chat or a QR code."],
        protect: ["Open important services from a bookmark or the app.", "Use a password manager: refusing to fill on a lookalike domain is a warning.", "Report the lure so colleagues are protected too."],
        remember: "Phishing does not crack the password — it persuades somebody to hand it over.",
        continueLabel: "Return to the briefing hub",
      },
    },
    {
      id: "b_teach_phishing_safe",
      type: "teach",
      teach: {
        outcome: "safe",
        body:
          "You treated the messages as evidence instead of instructions. Checking the sender and destination before acting blocks the phishing route that can place a working password in a breach dump.",
        spot: ["Lookalike domains and unexpected login pages.", "Urgency designed to make verification feel slow."],
        protect: ["Reach the service independently.", "Use a password manager and phishing-resistant MFA where available."],
        remember: "The safe moment is before the login page receives anything.",
        continueLabel: "Return to the briefing hub",
      },
    },
    { id: "b_done_phishing", type: "title", title: "Phishing lab complete", autoAdvanceMs: 850, guide: { title: "Complete", body: "Returning to the briefing hub." } },

    { id: "b_download_title", type: "title", environmentId: "office", title: "Live lab — Malicious download", autoAdvanceMs: 1400, guide: { title: "Download lab", body: "See how search can deliver infostealer malware." } },
    { id: "b_cam_personallaptop", type: "camera", waypointId: "wp_privacy_entrance", walkSfx: true },
    { id: "b_cam_personallaptop_close", type: "camera", waypointId: "wp_lounge_laptop" },
    { id: "b_personallaptop", type: "device", deviceId: "download_laptop", guide: { title: "Your own laptop", body: "Click the sticky note to open it." } },
    {
      id: "b_teach_download_safe",
      type: "teach",
      title: "You called it risky",
      teach: {
        outcome: "safe",
        body:
          "A browser game that needs a separate launcher doesn't add up. Calling that download risky, and closing the page, is what keeps an infostealer off the laptop.",
        remember: "A game that runs in the browser never needs an installer.",
        continueLabel: "See what the infostealer took",
      },
    },
    {
      id: "b_teach_download",
      type: "teach",
      teach: {
        outcome: "risky",
        body:
          "The advertised game really opened. Later, the shopping site also behaved normally — but the hidden infostealer watched the browser in parallel. It captured the demo login, checkout form, masked payment details and live session cookie, then sent the bundle to the attacker. The terminal you saw was an explanatory attacker view; no terminal window appeared for the user.",
        spot: ["A sponsored result on a lookalike domain.", "A site requiring an executable for content that should open in the browser.", "Normal-looking activity after installation does not prove the device is clean."],
        protect: ["Download only from the vendor or company software portal.", "Do not save work logins in a personal browser.", "If a suspicious file ran, report it immediately and revoke active sessions."],
        remember: "A working program can still carry an infostealer.",
        continueLabel: "Return to the briefing hub",
      },
    },
    { id: "b_done_download", type: "title", title: "Download lab complete", autoAdvanceMs: 850, guide: { title: "Complete", body: "Returning to the briefing hub." } },

    { id: "b_wifi_title", type: "title", title: "Live lab — Rogue Wi-Fi", autoAdvanceMs: 1400, guide: { title: "Wi-Fi lab", body: "A copied network name can lead into a certificate trap." } },
    { id: "b_wifi_pool_wide", type: "camera", environmentId: "pool", waypointId: "wp_breach_pool_wide" },
    { id: "b_wifi_pool_ipad", type: "camera", waypointId: "wp_breach_pool_ipad" },
    { id: "b_wifi", type: "device", deviceId: "holiday_wifi", guide: { title: "Choose the hotel network", body: "Use the iPad, inspect the browser warning, then hunt the rogue access point." }, completeOn: { action: "continue" } },
    {
      id: "b_teach_wifi",
      type: "teach",
      teach: {
        outcome: "recap",
        body:
          "The rogue network did not magically read an HTTPS password. It copied a trusted name and created a position from which to redirect the device, present a captive portal, or pressure the learner past a certificate warning. It is an enabler for phishing and attacks on poorly protected traffic.",
        spot: ["A familiar network name with no reliable way to confirm who broadcasts it.", "A certificate warning on a site that is normally trusted.", "A captive portal asking for company credentials."],
        protect: ["Confirm the network with staff or use mobile data.", "Never bypass a certificate warning to reach email or a login.", "Use the company VPN, but do not treat it as permission to ignore browser warnings."],
        remember: "HTTPS prevents simple Wi-Fi password sniffing; the attacker therefore tries to redirect or deceive you.",
        continueLabel: "Return to the briefing hub",
      },
    },
    { id: "b_done_wifi", type: "title", title: "Wi-Fi lab complete", autoAdvanceMs: 850, guide: { title: "Complete", body: "Returning to the briefing hub." } },

    { id: "b_usb_title", type: "title", environmentId: "office", title: "Live lab — BadUSB", autoAdvanceMs: 1400, guide: { title: "BadUSB lab", body: "An unexpected package is waiting on your desk." } },
    { id: "b_cam_usb", type: "camera", waypointId: "wp_desk_glide", walkSfx: true },
    { ...beat("b_usb_decision"), deviceId: "usb_decision", guide: { ...beat("b_usb_decision").guide!, title: "The suspicious delivery" } },
    beat("b_usb_consequence"),
    beat("b_teach_usb_risky"),
    { ...beat("b_usb_recovery"), deviceId: "usb_recovery", guide: { ...beat("b_usb_recovery").guide!, title: "The suspicious delivery" } },
    beat("b_teach_usb_hide"),
    beat("b_teach_usb_notreport"),
    beat("b_teach_usb_reported"),
    beat("b_usb_consequence_safe"),
    beat("b_teach_usb_safe"),
    { id: "b_done_usb", type: "title", title: "BadUSB lab complete", autoAdvanceMs: 850, guide: { title: "Complete", body: "Returning to the briefing hub." } },

    { id: "b_return_hub", type: "camera", environmentId: "office", waypointId: "wp_terminal_close", walkSfx: true },

    {
      id: "b_quiz",
      type: "quiz",
      title: "Check your understanding",
      quiz: [
        {
          id: "q_routes",
          title: "Question 1",
          prompt: "Your work password was found on a list of stolen logins. What does that tell you?",
          options: [
            { id: "a", label: "You must have clicked a phishing link", icon: "x" },
            { id: "b", label: "The password got out somehow — maybe a fake page, a hidden program, a hacked website, or someone watching — and it is not automatically your fault", correct: true, icon: "check" },
            { id: "c", label: "Nothing needs to happen as long as your computer still works normally", icon: "alert" },
          ],
          rationale: "A password can get out in several ways, and often the leak is at another company, not on your computer. The important thing is to act once you know.",
        },
        {
          id: "q_third_party",
          title: "Question 2",
          prompt: "A website you signed up for years ago gets hacked. What keeps that leak from also opening your work email?",
          options: [
            { id: "a", label: "Using a different password for every account", correct: true, icon: "lock" },
            { id: "b", label: "That website was never opened on your work laptop", icon: "x" },
            { id: "c", label: "Waiting for a warning email before doing anything", icon: "clock" },
          ],
          rationale: "Criminals try a stolen password on many sites. If each account has its own password, one leak cannot open the others.",
        },
        {
          id: "q_cookie",
          title: "Question 3",
          prompt: "Why is the \"stay signed in\" key on your device worth stealing?",
          options: [
            { id: "a", label: "It can let a criminal in without the password and without the phone code", correct: true, icon: "check" },
            { id: "b", label: "It shows every password you have ever used", icon: "x" },
            { id: "c", label: "It means two-step sign-in is pointless", icon: "alert" },
          ],
          rationale: "When you stay signed in, your device holds a key that proves you already logged in. Stealing it skips the login. Two-step sign-in still helps; if your computer is infected, sign out everywhere.",
        },
        {
          id: "q_market",
          title: "Question 4",
          prompt: "Why is getting into your real work mailbox so useful to a criminal?",
          options: [
            { id: "a", label: "They can send believable messages to your real contacts, and reach contracts and customer data", correct: true, icon: "check" },
            { id: "b", label: "They only gain some extra storage space", icon: "x" },
            { id: "c", label: "It shows them the company bank account automatically", icon: "alert" },
          ],
          rationale: "A real mailbox carries your name and your past emails, so fake invoices and requests look normal — and your colleagues become the next targets.",
        },
        {
          id: "q_response",
          title: "Question 5",
          prompt: "You have just learned your work login was stolen. What should you do first?",
          options: [
            { id: "a", label: "Change the password everywhere you used it, turn on two-step sign-in, and tell IT if a hidden program might be on your computer", correct: true, icon: "check" },
            { id: "b", label: "Reply with your current password so IT can check it", icon: "x" },
            { id: "c", label: "Wait and see if anyone actually signs in", icon: "clock" },
          ],
          rationale: "Changing the password makes the stolen one useless, two-step sign-in adds a second lock, and IT can sign you out everywhere. Real IT never asks you for your password.",
        },
      ],
    },
  ],

  edges: [
    { from: "b_intro", to: "b_cam_terminal" },
    { from: "b_cam_terminal", to: "b_hub" },
    { from: "b_hub", to: "b_deck_how_title", on: "deck_how" },
    { from: "b_hub", to: "b_deck_value_title", on: "deck_value" },
    { from: "b_hub", to: "b_phishing_title", on: "lab_phishing" },
    { from: "b_hub", to: "b_download_title", on: "lab_download" },
    { from: "b_hub", to: "b_wifi_title", on: "lab_wifi" },
    { from: "b_hub", to: "b_usb_title", on: "lab_usb" },
    { from: "b_hub", to: "b_quiz", on: "finish" },

    { from: "b_deck_how_title", to: "b_cam_meeting_how" },
    { from: "b_cam_meeting_how", to: "b_cam_tv_how" },
    { from: "b_cam_tv_how", to: "b_deck_how" },
    { from: "b_deck_how", to: "b_done_deck_how" },
    { from: "b_done_deck_how", to: "b_return_hub" },
    { from: "b_deck_value_title", to: "b_cam_meeting_value" },
    { from: "b_cam_meeting_value", to: "b_cam_tv_value" },
    { from: "b_cam_tv_value", to: "b_deck_value" },
    { from: "b_deck_value", to: "b_done_deck_value" },
    { from: "b_done_deck_value", to: "b_return_hub" },

    { from: "b_phishing_title", to: "b_cam_signin" },
    { from: "b_cam_signin", to: "b_desklogin" },
    { from: "b_desklogin", to: "b_desktop_pdf" },
    { from: "b_desktop_pdf", to: "b_desktop_mail" },
    { from: "b_desktop_mail", to: "b_cam_inbox" },
    { from: "b_cam_inbox", to: "b_inbox_triage" },
    { from: "b_inbox_triage", to: "b_teach_phishing_risky", on: "risky" },
    { from: "b_inbox_triage", to: "b_teach_phishing_safe", on: "safe" },
    { from: "b_teach_phishing_risky", to: "b_done_phishing" },
    { from: "b_teach_phishing_safe", to: "b_done_phishing" },
    { from: "b_done_phishing", to: "b_return_hub" },

    { from: "b_download_title", to: "b_cam_personallaptop" },
    { from: "b_cam_personallaptop", to: "b_cam_personallaptop_close" },
    { from: "b_cam_personallaptop_close", to: "b_personallaptop" },
    { from: "b_personallaptop", to: "b_teach_download" },
    { from: "b_personallaptop", to: "b_teach_download_safe", on: "safe" },
    { from: "b_teach_download_safe", to: "b_teach_download" },
    { from: "b_teach_download", to: "b_done_download" },
    { from: "b_done_download", to: "b_return_hub" },

    { from: "b_wifi_title", to: "b_wifi_pool_wide" },
    { from: "b_wifi_pool_wide", to: "b_wifi_pool_ipad" },
    { from: "b_wifi_pool_ipad", to: "b_wifi" },
    { from: "b_wifi", to: "b_teach_wifi" },
    { from: "b_teach_wifi", to: "b_done_wifi" },
    { from: "b_done_wifi", to: "b_return_hub" },

    { from: "b_usb_title", to: "b_cam_usb" },
    { from: "b_cam_usb", to: "b_usb_decision" },
    { from: "b_usb_decision", to: "b_usb_consequence", on: "risky" },
    { from: "b_usb_decision", to: "b_usb_consequence_safe", on: "safe" },
    { from: "b_usb_consequence", to: "b_teach_usb_risky" },
    { from: "b_teach_usb_risky", to: "b_usb_recovery" },
    { from: "b_usb_recovery", to: "b_teach_usb_hide", on: "risky" },
    { from: "b_usb_recovery", to: "b_teach_usb_notreport", on: "not_reported" },
    { from: "b_usb_recovery", to: "b_teach_usb_reported", on: "reported" },
    { from: "b_teach_usb_hide", to: "b_done_usb" },
    { from: "b_teach_usb_notreport", to: "b_done_usb" },
    { from: "b_teach_usb_reported", to: "b_done_usb" },
    { from: "b_usb_consequence_safe", to: "b_teach_usb_safe" },
    { from: "b_teach_usb_safe", to: "b_done_usb" },
    { from: "b_done_usb", to: "b_return_hub" },

    { from: "b_return_hub", to: "b_hub" },
  ],

  initialBeatId: "b_intro",
};
